import { Avatar, Box, Tooltip } from '@mantine/core';
import { ProviderRole } from '@bariendo/lib/constants';
import { Reference, Resource } from '@medplum/fhirtypes';
import { ResourceAvatar } from '@medplum/react';
import { IconUserCircle } from '@tabler/icons-react';
import { ReactElement } from 'react';
import { useProviderRole } from '@/hooks/useProviderRole';
import { getPersonInitials } from '@/services/person-initials';
import { getRoleColor } from '@/theme/roleColors';

/**
 * One person, anywhere in Orbit: card headers, care team rows, assignee slots,
 * list cells, related contacts. Photo or initials, plus the 2px ring in the
 * person's role color (BAR-2571) — Warm White when no role resolves, which is
 * also the pre-color look.
 *
 * Identify the person by `practitioner` (a Reference or resource — photos
 * render, and the role resolves from the directory by id) or by `name` alone
 * (initials only; the role resolves by display name). `role` overrides both.
 * Pass `resolveRole={false}` for someone who is NOT staff — a patient's
 * related contact, say — so a name that happens to match a teammate's never
 * borrows their ring.
 */
export interface TeammateAvatarProps {
  practitioner?: Reference | Resource;
  name?: string;
  role?: ProviderRole;
  /** Look the role up in the provider directory. Default true; false for non-staff. */
  resolveRole?: boolean;
  /** Diameter in px. Avatar sm (24) is the timeline default. */
  size?: number;
  /**
   * Tooltip and accessible name. With it the avatar is an image named `label`;
   * without it the avatar is decoration beside text that already says who
   * (initials stay readable, a photo keeps Medplum's alt).
   */
  label?: string;
}

const WARM_WHITE_RING = 'var(--ev-color-warm-white, #fafcf7)';
/** The teammate tint + ink every initials avatar in Orbit shares. */
export const TEAMMATE_TINT = 'rgba(36, 55, 33, 0.10)';
export const TEAMMATE_INK = 'var(--ev-color-green-dark, #243721)';

export function teammateRing(role: ProviderRole | undefined): string {
  return `0 0 0 2px ${getRoleColor(role)?.fill ?? WARM_WHITE_RING}`;
}

export function TeammateAvatar({
  practitioner,
  name,
  role,
  resolveRole = true,
  size = 24,
  label,
}: TeammateAvatarProps): ReactElement {
  const lookedUp = useProviderRole(resolveRole && !role ? (practitioner ?? name) : undefined);
  const ring = teammateRing(role ?? lookedUp);
  const named = label ? { role: 'img' as const, 'aria-label': label } : {};

  let avatar: ReactElement;
  if (practitioner) {
    const photo = <ResourceAvatar value={practitioner} size={size} radius="xl" style={{ boxShadow: ring }} />;
    avatar = label ? (
      <Box component="span" {...named} style={{ display: 'inline-flex', flexShrink: 0 }}>
        {photo}
      </Box>
    ) : (
      photo
    );
  } else {
    avatar = (
      <Avatar
        size={size}
        radius="xl"
        {...named}
        styles={{
          root: { backgroundColor: TEAMMATE_TINT, boxShadow: ring, flexShrink: 0 },
          placeholder: { fontSize: size >= 28 ? 12 : 11, fontWeight: 500, color: TEAMMATE_INK },
        }}
      >
        {/* No name to draw from: a person glyph rather than a bare "?". */}
        {name?.trim() ? getPersonInitials(name) : <IconUserCircle size={Math.round(size * 0.7)} stroke={1.5} />}
      </Avatar>
    );
  }

  return label ? (
    <Tooltip label={label} withArrow>
      {avatar}
    </Tooltip>
  ) : (
    avatar
  );
}
